import React from "react";
import { Modal } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

import { Alert, Button, CloseButton, HookSelectFormGroup } from "components";
import { DebounceTimeout } from "globalConstants";
import {
	editAgentResidualsStatusPermissionSet,
	hasAllPermissions,
} from "helpers";
import { useSetAgentResidualsStatusMutation } from "queries";
import { ResidualsStatusTypes, WithholdStatuses } from "types";
import { required } from "validators/validation";

import { ReactComponent as WarningIcon } from "static/images/warning-alert.svg";
import styles from "./changeResidualStatusModal.module.scss";

type ChangeResidualStatusModalProps = {
	show: boolean;
	onHide: () => void;
	onStatusChange?: Function;
	agentId: string;
};

const ChangeResidualStatusModal = ({
	show = false,
	onHide,
	onStatusChange,
	agentId,
}: ChangeResidualStatusModalProps) => {
	const formMethods = useForm({
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: {},
	});

	const {
		handleSubmit,
		formState: { isDirty },
	} = formMethods;

	const isResidualsStatusEditable = hasAllPermissions(
		editAgentResidualsStatusPermissionSet,
	);

	const residualsStatuses = ResidualsStatusTypes.getOptions();

	const {
		mutate: changeAgentResidualsStatus,
		isLoading: isLoadingChangeStatus,
	} = useSetAgentResidualsStatusMutation({ withoutLoading: true });

	const handleSubmitForm = values => {
		const isWithholdResiduals =
			values?.residualsStatus?.name ===
			ResidualsStatusTypes.WithholdResiduals.name;
		const withholdReason = isWithholdResiduals
			? WithholdStatuses.TerminatedAgentPartner.value
			: undefined;

		changeAgentResidualsStatus({
			agentId,
			residualsStatus: values?.residualsStatus?.value,
			withholdReason,
			onSuccess: () => {
				onStatusChange?.();
				onHide();
			},
		});
	};

	return (
		<Modal show={show} onHide={onHide} animation={false} centered>
			<FormProvider {...formMethods}>
				<form onSubmit={handleSubmit(handleSubmitForm)}>
					<Modal.Header>
						<Modal.Title as="h6">
							<div className="text-warning mb-2">
								<WarningIcon width="30" />
							</div>
							<p className={styles.title}>
								This agent has active merchants assigned.
							</p>
							<p className={styles.title}>
								Please assign ongoing residuals status upon
								deactivation.
							</p>
						</Modal.Title>
						<CloseButton onClick={onHide} />
					</Modal.Header>
					<Modal.Body>
						<div className={styles.content}>
							<HookSelectFormGroup
								label="Residuals Status"
								options={residualsStatuses}
								getOptionLabel={option => option.displayName}
								getOptionValue={option => option.value}
								placeholder="Please Select"
								name="residualsStatus"
								blurLoading={isLoadingChangeStatus}
								isSearchable={false}
								isDisabled={!isResidualsStatusEditable}
								cacheOptions
								defaultOptions
								defaultValue={null}
								debounceTimeout={DebounceTimeout}
								showRequiredMark
								colLabel={{ sm: 12 }}
								colSelect={{ sm: 12 }}
								rules={{
									validate: {
										required,
									},
								}}
							/>
							{!isResidualsStatusEditable && (
								<Alert
									variant="notice"
									className="mt-3"
									message="You do not have permission to change residuals
								status. Contact your administrator for more info."
								/>
							)}
						</div>
					</Modal.Body>
					<Modal.Footer className="justify-content-center pb-5">
						<Button
							type="submit"
							value="Save"
							variant="primary"
							disabled={!isDirty || isLoadingChangeStatus}
						/>
					</Modal.Footer>
				</form>
			</FormProvider>
		</Modal>
	);
};

export default ChangeResidualStatusModal;
